import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Registration.css";
import authService from "../../services/authService";
import SuccessModal from "../Common/SuccessModal";
import ErrorModal from "../Common/ErrorModal";
import TermsModal from "../Common/TermsModal";
import easyAuctionLogo from "../../assets/easyauction.png";

/* ---------- TYPES ---------- */
interface RegistrationFormState {
  phone: string;
  email: string;
  name: string;
  companyName: string;
  companyAddress: string;
  companyProductService: string;
  gstnNumber: string;
  otp: string;
}

interface FormErrors {
  phone?: string;
  email?: string;
  name?: string;
  companyName?: string;
  companyAddress?: string;
  companyProductService?: string;
  gstnNumber?: string;
  otp?: string;
}

/* ---------- COMPONENT ---------- */
export default function Registration() {
  const navigate = useNavigate();

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [maskedEmail, setMaskedEmail] = useState<string>("");
  const [otpTimer, setOtpTimer] = useState(0);

  // Modal states
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
  });

  const [form, setForm] = useState<RegistrationFormState>({
    phone: "",
    email: "",
    name: "",
    companyName: "",
    companyAddress: "",
    companyProductService: "",
    gstnNumber: "",
    otp: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  /* ---------- OTP TIMER ---------- */
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  /* ---------- HELPERS ---------- */
  const validateEmail = (email: string): boolean =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|org)$/i.test(email);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let filtered = value;

    setErrors((prev) => ({ ...prev, [name]: undefined }));

    switch (name) {
      case "phone":
        filtered = value.replace(/\D/g, "").slice(0, 10);
        break;
      case "name":
      case "companyName":
        filtered = value.replace(/[^a-zA-Z\s\-.,&']/g, "");
        break;
      case "email":
        filtered = value.replace(/[^a-zA-Z0-9@.\-_+]/g, "");
        if (otpSent) {
          // The OTP was issued to the previous address
          setOtpSent(false);
          setSessionId("");
          setOtpTimer(0);
          setForm((prev) => ({ ...prev, otp: "" }));
        }
        break;
      case "companyAddress":
        filtered = value.replace(/[^a-zA-Z0-9\s\-.,#/]/g, "");
        break;
      case "companyProductService":
        filtered = value.replace(/[^a-zA-Z0-9\s\-.,&']/g, "");
        break;
      case "gstnNumber":
        filtered = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
        break;
      case "otp":
        filtered = value.replace(/\D/g, "").slice(0, 4);
        break;
      default:
        filtered = value;
    }

    setForm((prev) => ({ ...prev, [name]: filtered }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    else if (form.phone.length !== 10) newErrors.phone = "Exactly 10 digits";
    else if (!/^[6-9]\d{9}$/.test(form.phone))
      newErrors.phone = "Must start with 6-9";

    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(form.email))
      newErrors.email = "Must end with .com, .in or .org";

    if (!form.companyName.trim()) newErrors.companyName = "Required";
    else if (form.companyName.trim().length < 4)
      newErrors.companyName = " Enter at least 4-5 characters";

    if (form.name && form.name.trim().length < 4)
      newErrors.name = "Enter at Least 4-5 Characters";
    else if (!form.name.trim()) newErrors.name = "Required";
    
    if (form.gstnNumber && form.gstnNumber.length !== 15)
      newErrors.gstnNumber = "GSTN must be exactly 15 characters";
    else if (form.gstnNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstnNumber))
      newErrors.gstnNumber = "Invalid GSTN format";

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  /* ---------- OTP HANDLERS ---------- */
  const handleSendOTP = async () => {
    // Validate email first
    const newErrors: FormErrors = {};
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(form.email))
      newErrors.email = "Must end with .com, .in or .org";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSendingOtp(true);
    try {
      const res = await authService.sendRegistrationOTP({
        email: form.email.trim(),
        person_name: form.name.trim() || undefined,
      });

      if (res.success && res.sessionId) {
        setSessionId(res.sessionId);
        setMaskedEmail(res.maskedEmail || "");
        setOtpSent(true);
        setForm((prev) => ({ ...prev, otp: "" }));
        setOtpTimer(60); // cooldown before another OTP can be requested
      } else {
        throw new Error(res.message || "Failed to send OTP");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err?.message || "Failed to send OTP. Please try again.",
      });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!form.otp || form.otp.length !== 4) {
      setErrors((prev) => ({ ...prev, otp: "Enter 4-digit OTP" }));
      return;
    }

    setVerifyingOtp(true);
    try {
      const res = await authService.verifyRegistrationOTP({
        sessionId,
        otp: form.otp,
        email: form.email.trim(),
      });

      if (res.success) {
        setOtpVerified(true);
      } else {
        throw new Error(res.message || "Invalid OTP");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err?.message || "Invalid OTP. Please try again.",
      });
    } finally {
      setVerifyingOtp(false);
    }
  };

  /* ---------- SUBMIT ---------- */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if OTP is verified
    if (!otpVerified) {
      setErrorModal({
        isOpen: true,
        message: "Please verify your email with OTP first.",
      });
      return;
    }

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const res = await authService.register({
        phone_number: `+91${form.phone.trim()}`,
        email: form.email.trim(),
        sessionId,
        person_name: form.name || undefined,
        company_name: form.companyName.trim(),
        company_address: form.companyAddress || undefined,
        company_product_service: form.companyProductService || undefined,
        gstn_number: form.gstnNumber || undefined,
      });

      if (res.success) {
        if (res.user) localStorage.setItem("auctionUser", JSON.stringify(res.user));
        if (res.token) localStorage.setItem("authToken", res.token);
        setSuccessModal({
          isOpen: true,
          message: res.message || "Registration successful! Please log in to continue.",
        });
        // Navigate after modal is shown
        setTimeout(() => navigate("/login"), 2000);
      } else {
        throw new Error(res.message || "Registration failed");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err?.message || "Registration failed. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="ap-reg-wrapper">
      <div className="ap-reg-card">
        <header className="ap-reg-header">
          <div className="ap-reg-logo-badge">
            <img src={easyAuctionLogo} alt="EasyEAuction logo" className="ap-reg-logo-img" />
          </div>
          <h1 className="ap-reg-title">Create Your Account</h1>
          <p className="ap-reg-sub">Register to participate in auctions</p>
        </header>

        <form className="ap-reg-form" onSubmit={handleSave} noValidate>
          <div className="ap-reg-grid">
            {/* Phone */}
            <div className="ap-reg-field">
              <label htmlFor="phone" className="ap-reg-label">
                Phone Number<span className="ap-reg-required">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                required
                inputMode="numeric"
                pattern="\d{10}"
                maxLength={10}
                className={`ap-reg-input ${errors.phone ? "error" : ""}`}
                placeholder="Enter your phone number"
                value={form.phone}
                onChange={handleChange}
                title="Enter exactly 10 digits"
              />
              {errors.phone && (
                <span className="ap-reg-error">{errors.phone}</span>
              )}
              {!errors.phone && (
                <small className="ap-reg-hint">
                  Enter exactly 10 digits (no spaces or symbols). You will log in with this number.
                </small>
              )}
            </div>

            {/* Email + OTP verification */}
            <div className="ap-reg-field">
              <label htmlFor="email" className="ap-reg-label">
                Email<span className="ap-reg-required">*</span>
              </label>
              <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                <input
                  id="email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  className={`ap-reg-input ${errors.email ? "error" : ""}`}
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={handleChange}
                  title="Email must end with .com, .in, or .org"
                  disabled={otpVerified}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="ap-reg-btn"
                  onClick={handleSendOTP}
                  disabled={sendingOtp || !form.email || otpVerified || otpTimer > 0}
                  style={{
                    minWidth: "100px",
                    fontSize: "14px",
                    padding: "8px 12px",
                    backgroundColor: otpVerified ? "#10b981" : undefined,
                  }}
                >
                  {otpVerified ? "✓ Verified" : sendingOtp ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              </div>
              {errors.email && (
                <span className="ap-reg-error">{errors.email}</span>
              )}
              {!errors.email && !otpSent && !otpVerified && (
                <small className="ap-reg-hint">
                  We'll email a 4-digit OTP to verify this address (.com, .in or .org)
                </small>
              )}
              {!errors.email && otpSent && !otpVerified && (
                <small className="ap-reg-hint" style={{ color: "#1261d6" }}>
                  ✉️ OTP sent to {maskedEmail || "your email"}. Check your inbox (and spam) and enter it below.
                </small>
              )}
              {otpVerified && (
                <small className="ap-reg-hint" style={{ color: "#10b981" }}>
                  ✓ Email verified successfully
                </small>
              )}
            </div>

            {/* OTP Field */}
            {otpSent && !otpVerified && (
              <div className="ap-reg-field">
                <label htmlFor="otp" className="ap-reg-label">
                  Enter OTP<span className="ap-reg-required">*</span>
                </label>
                <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                  <input
                    id="otp"
                    type="text"
                    name="otp"
                    inputMode="numeric"
                    maxLength={4}
                    className={`ap-reg-input ${errors.otp ? "error" : ""}`}
                    placeholder="Enter 4-digit OTP"
                    value={form.otp}
                    onChange={handleChange}
                    title="Enter 4-digit OTP"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="ap-reg-btn"
                    onClick={handleVerifyOTP}
                    disabled={verifyingOtp || !form.otp || form.otp.length !== 4}
                    style={{
                      minWidth: "100px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  >
                    {verifyingOtp ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
                {errors.otp && (
                  <span className="ap-reg-error">{errors.otp}</span>
                )}
                {!errors.otp && (
                  <small className="ap-reg-hint" style={{ color: "#1261d6" }}>
                    Enter the 4-digit OTP sent to your email
                  </small>
                )}
                <div style={{ marginTop: "4px" }}>
                  <small className="ap-reg-hint">
                    {otpTimer > 0
                      ? `You can request a new OTP in ${Math.floor(otpTimer / 60)}:${(otpTimer % 60).toString().padStart(2, "0")}`
                      : "Didn't get it? Use Resend OTP above."}
                  </small>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="ap-reg-field">
              <label htmlFor="name" className="ap-reg-label">
                Person Name<span className="ap-reg-required">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                autoComplete="name"
                minLength={2}
                maxLength={50}
                className={`ap-reg-input ${errors.name ? "error" : ""}`}
                placeholder="Enter your Full Name"
                value={form.name}
                onChange={handleChange}
                title="Letters, spaces, hyphens, dots only"
              />
              {errors.name && (
                <span className="ap-reg-error">{errors.name}</span>
              )}
              {!errors.phone && (
                <small className="ap-reg-hint">Enter Your Full Name</small>
              )}
            </div>

            {/* Company Name */}
            <div className="ap-reg-field ap-reg-field--full">
              <label htmlFor="companyName" className="ap-reg-label">
                Company Name<span className="ap-reg-required">*</span>
              </label>
              <input
                id="companyName"
                type="text"
                name="companyName"
                required
                autoComplete="organization"
                minLength={2}
                maxLength={100}
                className={`ap-reg-input ${errors.companyName ? "error" : ""}`}
                placeholder="Enter your Company Name"
                value={form.companyName}
                onChange={handleChange}
                title="2-100 characters"
              />
              {errors.companyName && (
                <span className="ap-reg-error">{errors.companyName}</span>
              )}
              {!errors.companyName && (
                <small className="ap-reg-hint">Enter Your Company Name</small>
              )}
            </div>

            {/* Company Product / Service */}
            <div className="ap-reg-field ap-reg-field--full">
              <label htmlFor="companyProductService" className="ap-reg-label">
                <span>
                  Company Product/Service
                </span>
              </label>
              <input
                id="companyProductService"
                type="text"
                name="companyProductService"
                autoComplete="off"
                maxLength={200}
                className={`ap-reg-input ${
                  errors.companyProductService ? "error" : ""
                }`}
                placeholder="e.g., Construction Materials, IT Service (optional)"
                value={form.companyProductService}
                onChange={handleChange}
                title="Describe your products or services (optional)"
              />
              {errors.companyProductService && (
                <span className="ap-reg-error">
                  {errors.companyProductService}
                </span>
              )}
              {!errors.companyProductService && (
                <small className="ap-reg-hint">
                  What does your company sell or provide? (optional)
                </small>
              )}
            </div>

            {/* Company Address */}
            <div className="ap-reg-field ap-reg-field--full">
              <label htmlFor="companyAddress" className="ap-reg-label">
                Company Address
              </label>
              <input
                id="companyAddress"
                type="text"
                name="companyAddress"
                autoComplete="street-address"
                maxLength={200}
                className={`ap-reg-input ${
                  errors.companyAddress ? "error" : ""
                }`}
                placeholder="123 Business Street, City, State"
                value={form.companyAddress}
                onChange={handleChange}
                title="Enter a valid address"
              />
              {errors.companyAddress && (
                <span className="ap-reg-error">{errors.companyAddress}</span>
              )}
              {!errors.phone && (
                <small className="ap-reg-hint">Enter Your Company Address</small>
              )}
            </div>

            {/* GSTN Number */}
            <div className="ap-reg-field ap-reg-field--full">
              <label htmlFor="gstnNumber" className="ap-reg-label">
                GSTN Number
              </label>
              <input
                id="gstnNumber"
                type="text"
                name="gstnNumber"
                autoComplete="off"
                maxLength={15}
                className={`ap-reg-input ${
                  errors.gstnNumber ? "error" : ""
                }`}
                placeholder="e.g., 22AAAAA0000A1Z5"
                value={form.gstnNumber}
                onChange={handleChange}
                title="Enter 15-character GSTN (optional)"
              />
              {errors.gstnNumber && (
                <span className="ap-reg-error">{errors.gstnNumber}</span>
              )}
              {!errors.gstnNumber && (
                <small className="ap-reg-hint">
                  GST Number (optional, 15 characters)
                </small>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="ap-reg-actions">
            <button
              type="submit"
              className="ap-reg-btn"
              disabled={submitting || !form.phone || !form.companyName || !otpVerified}
              title={!otpVerified ? "Please verify your email with OTP first" : ""}
            >
              {submitting ? "Registering…" : "Register"}
            </button>

            <button
              type="button"
              className="ap-reg-btn-secondary"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </button>
          </div>
          
          {!otpVerified && otpSent && (
            <p style={{ textAlign: "center", color: "#f59e0b", fontSize: "14px", marginTop: "8px" }}>
              ⚠️ Please verify your email with OTP to continue
            </p>
          )}

          {/* Footer */}
          <p className="ap-reg-disclaimer">
            By continuing you agree to our{" "}
            <a role="button" onClick={() => setIsTermsModalOpen(true)}>
              Terms
            </a>{" "}
            &{" "}
            <a role="button" onClick={() => setIsTermsModalOpen(true)}>
              Privacy Policy
            </a>
            .
            <TermsModal
              isOpen={isTermsModalOpen}
              onClose={() => setIsTermsModalOpen(false)}
            />
          </p>
        </form>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => {
          setSuccessModal({ isOpen: false, message: "" });
          // Only navigate to login after actual registration, not during OTP flow
        }}
        title="Registration Successful!"
        message={successModal.message}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => {
          setErrorModal({ isOpen: false, message: "" });
        }}
        title="Registration Failed"
        message={errorModal.message}
      />
    </div>
  );
}

